import React, { useEffect, useRef, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import config from '../../config/env';
import '../../styles/cookie-consent.css';

/**
 * The cookie dialog, shown once until the choice expires and again from the
 * sidebar's "Cookies" link. Two groups only: what signing in needs (always
 * on) and comfort settings, which the person may keep off. "Continue without
 * accepting" is as visible as "Accept all", as the CNIL asks; there is no
 * third party to list, so the text says what the application really keeps.
 */
export const CookieConsent: React.FC = () => {
  const { t } = useI18n();
  const { consent, open, acceptAll, refuseAll, save } = useCookieConsent();
  const [manage, setManage] = useState(false);
  const [comfort, setComfort] = useState<boolean>(consent?.preferences ?? true);
  const acceptButton = useRef<HTMLButtonElement>(null);
  useLockBodyScroll(open); // the page behind stays where it is

  useEffect(() => {
    if (!open) return;
    setManage(false);
    setComfort(consent?.preferences ?? true);
    acceptButton.current?.focus();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps -- reset on opening only

  if (!open) return null;

  return (
    <div className="cc-overlay" role="presentation">
      <section className="cc-dialog" role="dialog" aria-modal="true" aria-labelledby="cc-title" data-testid="cookie-consent">
        <button type="button" className="cc-skip" data-testid="cc-refuse" onClick={refuseAll}>
          {t('cookies.refuse')} <span aria-hidden="true">→</span>
        </button>

        <img src={config.ui.logoUrl} alt="" className="cc-logo" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        <h2 id="cc-title" className="cc-title">{t('cookies.title')}</h2>
        <p className="cc-intro">{t('cookies.intro')}</p>

        {!manage ? (
          <>
            <div className="cc-categories">
              <div className="cc-category" data-testid="cc-group-necessary">
                <div>
                  <strong>{t('cookies.necessary.name')}</strong>
                  <p>{t('cookies.necessary.summary')}</p>
                </div>
                <span className="cc-always">{t('cookies.alwaysOn')}</span>
              </div>
              <div className="cc-category" data-testid="cc-group-comfort">
                <div>
                  <strong>{t('cookies.comfort.name')}</strong>
                  <p>{t('cookies.comfort.summary')}</p>
                </div>
                <span className="cc-always">{t('cookies.withConsent')}</span>
              </div>
            </div>
            <p className="cc-note">{t('cookies.note')}</p>
            <div className="cc-actions">
              <button type="button" className="btn btn-secondary" data-testid="cc-manage" onClick={() => setManage(true)}>{t('cookies.manage')}</button>
              <button type="button" className="btn btn-primary" data-testid="cc-accept" ref={acceptButton} onClick={acceptAll}>{t('cookies.acceptAll')}</button>
            </div>
          </>
        ) : (
          <>
            <div className="cc-categories">
              <div className="cc-category" data-testid="cc-category-necessary">
                <div>
                  <strong>{t('cookies.necessary.name')}</strong>
                  <p>{t('cookies.necessary.detail')}</p>
                </div>
                <span className="cc-always">{t('cookies.alwaysOn')}</span>
              </div>
              <div className="cc-category" data-testid="cc-category-comfort">
                <div>
                  <strong>{t('cookies.comfort.name')}</strong>
                  <p>{t('cookies.comfort.detail')}</p>
                </div>
                <label className="cc-switch">
                  <input type="checkbox" data-testid="cc-toggle-comfort" aria-label={t('cookies.comfort.name')} checked={comfort} onChange={(e) => setComfort(e.target.checked)} />
                  <span className="cc-slider" aria-hidden="true" />
                </label>
              </div>
            </div>
            <p className="cc-note">{t('cookies.keycloak')}</p>
            <div className="cc-actions">
              <button type="button" className="btn btn-secondary" data-testid="cc-save" onClick={() => save(comfort)}>{t('cookies.save')}</button>
              <button type="button" className="btn btn-primary" data-testid="cc-accept-all" onClick={acceptAll}>{t('cookies.acceptAll')}</button>
            </div>
          </>
        )}
      </section>
    </div>
  );
};
